import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import type MapComponent from '/@/components/map';
import type { useFieldById, useMyFields } from '/@/hooks/fields';
import { useAllFormatedMaps, useHumidityMaps } from '/@/hooks/maps';
import { getEnableSpodoptera, getMapsEnabled, getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { convertCountryBoundsToGoogleLatLng } from '/@/views/fields/field-new-v2/utils';
import type { FieldListV2FormType } from '../..';
import FieldMap from './field-map';
import { getOverlayData } from './overlay-data';

// Types for better type safety
interface MapData {
    slug: string;
    type: string;
    versions: Array<{ versionNumber: number }>;
}

interface OverlaySelection {
    overlay: { type: string; slug: string };
    version?: { versionNumber: number };
}

interface OverlayChangeParams {
    type: string;
    version: string;
}

type OverlayChangeHandlerParams =
    | { type: string; version: string }
    | { overlay: { slug: string }; version: { versionNumber: number } };

const useMap = ({
    allFieldsQuery,
    fieldQuery,
}: {
    allFieldsQuery: ReturnType<typeof useMyFields>;
    fieldQuery: ReturnType<typeof useFieldById>;
}) => {
    const mapRef = useRef<typeof MapComponent>(null);
    const country = useSelector(getSessionCountry);

    useEffect(() => {
        if (allFieldsQuery.data?.results?.length > 0) {
            mapRef.current?.focusMap();
        } else if (country) {
            const googleBounds = convertCountryBoundsToGoogleLatLng(country.bounds);
            mapRef.current?.fitBounds(googleBounds);
        }
    }, [allFieldsQuery.data, country]);

    useEffect(() => {
        if (fieldQuery.data) {
            mapRef.current?.fitPoints(fieldQuery.data.geometry, undefined, false);
        }
    }, [fieldQuery.data]);

    return {
        mapRef,
    };
};

const FieldListV2Map = ({
    form,
    allFieldsQuery,
    fieldQuery,
}: {
    form: UseFormReturn<FieldListV2FormType>;
    allFieldsQuery: ReturnType<typeof useMyFields>;
    fieldQuery: ReturnType<typeof useFieldById>;
}) => {
    const { mapRef } = useMap({ allFieldsQuery, fieldQuery });

    const country = useSelector(getSessionCountry);
    const mapsQuery = useAllFormatedMaps({
        country: country.code,
    });
    const humidityMapsQuery = useHumidityMaps({ country: country.code });

    const [showFrostRiskMap, setShowFrostRiskMap] = useState(false);
    const [showMaps, setShowMaps] = useState(false);
    const [selectedOverlay, setSelectedOverlay] = useState<OverlaySelection | undefined>();
    const enabledSpodoptera = useSelector(getEnableSpodoptera);

    // Memoize filtered maps to avoid unnecessary recalculations
    const filteredMaps = useMemo(() => {
        return (mapsQuery.data || []).filter((map: MapData) => {
            if (map.slug === 'spodoptera-forecast') {
                return enabledSpodoptera;
            }
            return true;
        });
    }, [mapsQuery.data, enabledSpodoptera]);

    // Memoize parseOverlay function
    const parseOverlay = useCallback(
        (selected: OverlaySelection | undefined) => {
            if (!selected) return undefined;

            return filteredMaps.find(
                ({ type, slug }) => type === selected.overlay.type && slug === selected.overlay.slug
            )
                ? selected
                : undefined;
        },
        [filteredMaps]
    );

    // Memoize handleOverlayChange function
    const handleOverlayChange = useCallback(
        ({ type, version }: OverlayChangeParams) => {
            const overlay = filteredMaps.find((map: MapData) => map.slug === type);
            if (!overlay) return;

            const versionFound = overlay.versions.find(({ versionNumber }) => versionNumber === Number(version));
            if (!versionFound) return;

            setSelectedOverlay({ overlay, version: versionFound });
        },
        [filteredMaps]
    );

    // Memoize humidity click handler
    const handleHumidityClick = useCallback(() => {
        const humidityMap = humidityMapsQuery.data?.[0] as MapData;
        const humidityVersion = humidityMap?.versions?.[0];

        if (selectedOverlay && humidityMap === selectedOverlay.overlay) {
            setShowFrostRiskMap(false);
            setShowMaps(false);
            setSelectedOverlay(undefined);
        } else {
            mpTrackEvent('Soil moisture map open', undefined);
            setShowFrostRiskMap(false);
            setShowMaps(false);

            if (humidityMap && humidityVersion) {
                setSelectedOverlay({
                    overlay: humidityMap,
                    version: humidityVersion,
                });
            }
        }
    }, [selectedOverlay, humidityMapsQuery.data]);

    // Memoize frost risk handler
    const handleShowFrostRisk = useCallback(() => {
        if (!showFrostRiskMap) {
            mpTrackEvent('Frost risk map open', undefined);
        }
        setShowFrostRiskMap(!showFrostRiskMap);
        setShowMaps(false);
        setSelectedOverlay(undefined);
    }, [showFrostRiskMap]);

    // Memoize maps handler
    const handleShowMaps = useCallback(() => {
        if (!showMaps) {
            mpTrackEvent('Incidences map open', undefined);
        }
        setShowMaps(!showMaps);
        setShowFrostRiskMap(false);
        setSelectedOverlay(undefined);
    }, [showMaps]);

    // Memoize marker click handler
    const handleMarkerClick = useCallback(
        (field: { id: number }) => {
            mpTrackEvent('Field detail opened', {
                entry_source: 'map',
            });
            form.setValue('fieldId', field.id);
        },
        [form]
    );

    // Memoize overlay change handler for FieldMap
    const handleFieldMapOverlayChange = useCallback(
        (overlay: OverlayChangeHandlerParams) => {
            if (overlay?.type && overlay?.version) {
                return handleOverlayChange({ type: overlay.type, version: overlay.version });
            }
            if (overlay?.overlay?.slug && overlay.version?.versionNumber) {
                return handleOverlayChange({
                    type: overlay.overlay.slug,
                    version: overlay.version.versionNumber.toString(),
                });
            }
        },
        [handleOverlayChange]
    );

    // Memoize fields data
    const fields = useMemo(() => allFieldsQuery.data?.results || [], [allFieldsQuery.data?.results]);

    // Memoize maps data
    const maps = useMemo(() => mapsQuery.data || [], [mapsQuery.data]);

    const mapsEnabled = useSelector(getMapsEnabled);

    // Memoize humidity maps data
    const humidityMaps = useMemo(() => humidityMapsQuery.data || [], [humidityMapsQuery.data]);

    return (
        <FieldMap
            fields={fields}
            selectedField={fieldQuery.data}
            handleMarkerClick={handleMarkerClick}
            showFrostRiskMap={showFrostRiskMap}
            selectedOverlay={selectedOverlay}
            handleOverlayChange={handleFieldMapOverlayChange}
            showMaps={showMaps}
            parseOverlay={parseOverlay}
            overlayData={getOverlayData()}
            maps={maps}
            setMapRef={mapRef}
            offline={false}
            humidityMaps={humidityMaps}
            handleHumidityClick={handleHumidityClick}
            handleShowFrostRisk={handleShowFrostRisk}
            handleShowMaps={handleShowMaps}
            showTrialNetwork
            mapsEnabled={mapsEnabled}
        />
    );
};

export default FieldListV2Map;
